// M22 smart object pick: the object mattes of a photo's object masks (local masks of kind 'object').
// Each matte is the SlimSAM result for the mask's points and edge refine, cached in memory and in IndexedDB
// by photo + points + edge, so reopening a photo or exporting does not run the model again. The model
// files are self-hosted (public/models, `npm run fetch-models -- sam-encoder sam-decoder`), downloaded by
// the app from its own server the first time and kept in IndexedDB like IS-Net; not precached.
import * as Comlink from 'comlink';
import { create } from 'zustand';
import { createStore, get, set, type UseStore } from 'idb-keyval';
import { segWorker, MATTE_EDGE } from './matte';
import { decodePool } from '../../io/decode/pool';
import type { Matte } from './matteMath';
import type { LibraryPhoto } from '../../state/library';
import type { LocalMask, ObjectGeom, Settings } from '../../model/types';

type Status = { state: 'running' | 'error' | 'missing'; message?: string };
export const useObjectMasks = create<{
  /** By mask id. */
  status: Record<string, Status>;
  /** Bumped when a matte becomes available (the viewer re-reads). */
  version: number;
  available: boolean | null;
  lastRun: { encodeMs: number; decodeMs: number; ep: string } | null;
}>(() => ({ status: {}, version: 0, available: null, lastRun: null }));

export const objectStats = { inferences: 0 };

function fnv(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}
const photoKey = (p: LibraryPhoto) => `${p.name}|${p.size}|${p.lastModified}`;
export const objectKey = (p: LibraryPhoto, g: ObjectGeom) =>
  `sam-${fnv(photoKey(p))}-${fnv(JSON.stringify([g.points, g.edge]))}`;

let db: UseStore | null = null;
const store = () => (db ??= createStore('epb-objects', 'mattes'));
const memory = new Map<string, Matte>();
const remember = (k: string, m: Matte) => {
  memory.delete(k);
  memory.set(k, m);
  while (memory.size > 12) memory.delete(memory.keys().next().value as string);
};

const objectMasks = (s: Settings) =>
  s.masks.filter((m): m is LocalMask & { geom: ObjectGeom } => m.kind === 'object');

export async function objectModelAvailable(): Promise<boolean> {
  const cur = useObjectMasks.getState().available;
  if (cur !== null) return cur;
  const a = await segWorker()
    .samAvailable()
    .catch(() => false);
  useObjectMasks.setState({ available: a });
  return a;
}

/** The mattes already in memory (synchronous, for the viewer). */
export function objectMattesInMemory(p: LibraryPhoto, s: Settings) {
  const out: { id: string; key: string; width: number; height: number; data: Uint8Array }[] = [];
  for (const m of objectMasks(s)) {
    if (!m.geom.points.length) continue;
    const key = objectKey(p, m.geom);
    const hit = memory.get(key);
    if (hit) out.push({ id: m.id, key, ...hit });
  }
  return out;
}

async function ensureImage(p: LibraryPhoto) {
  const w = segWorker();
  const k = photoKey(p);
  if (await w.hasImage(k)) return;
  const small = await decodePool().small(p.file, MATTE_EDGE);
  await w.setImage(k, Comlink.transfer(small, [small.data.buffer as ArrayBuffer]));
}

const setStatus = (id: string, st: Status | null) =>
  useObjectMasks.setState((x) => {
    const status = { ...x.status };
    if (st) status[id] = st;
    else delete status[id];
    return { status };
  });

const inflight = new Map<string, Promise<Matte>>();
async function objectMatte(p: LibraryPhoto, id: string, g: ObjectGeom): Promise<Matte> {
  const key = objectKey(p, g);
  const m = memory.get(key);
  if (m) return m;
  const running = inflight.get(key);
  if (running) return running;
  const job = (async () => {
    const stored = await get<Matte>(key, store()).catch(() => undefined);
    if (stored?.data) {
      remember(key, stored);
      return stored;
    }
    if (!(await objectModelAvailable())) {
      setStatus(id, { state: 'missing' });
      throw new Error('object model not available');
    }
    setStatus(id, { state: 'running' });
    try {
      await ensureImage(p);
      const r = await segWorker().samSegment(photoKey(p), g.points, g.edge);
      objectStats.inferences++;
      useObjectMasks.setState({ lastRun: { encodeMs: r.encodeMs, decodeMs: r.decodeMs, ep: r.ep } });
      remember(key, r.matte);
      await set(key, r.matte, store()).catch(() => undefined);
      setStatus(id, null);
      useObjectMasks.setState((x) => ({ version: x.version + 1 }));
      return r.matte;
    } catch (e) {
      setStatus(id, { state: 'error', message: (e as Error).message });
      throw e;
    }
  })().finally(() => inflight.delete(key));
  inflight.set(key, job);
  return job;
}

/** Every object matte the settings call for (computing missing ones one after another). */
export async function objectMattesFor(p: LibraryPhoto, s: Settings) {
  const out: { id: string; key: string; width: number; height: number; data: Uint8Array }[] = [];
  for (const m of objectMasks(s)) {
    if (!m.geom.points.length) continue;
    try {
      const matte = await objectMatte(p, m.id, m.geom);
      out.push({ id: m.id, key: objectKey(p, m.geom), ...matte });
    } catch {
      /* status shows the error; the mask stays empty */
    }
  }
  return out;
}
